import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, takeUntil, lastValueFrom, combineLatest, debounceTime, firstValueFrom, startWith, finalize, distinctUntilChanged, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TiresService } from 'src/app/pages/marketplace/tires/services/tires.service';
import { AddStockDialogData, ProductLotEntry, SaveLotDTO, ValueLabelOption } from './models/add-stock-manually-model';
import { HTTP_STATUS, NOISE_CATEGORY_OPTIONS, QUALITY_OPTIONS, ROLLING_RESISTANCE_OPTIONS, SEASON_OPTIONS, SNACKBAR_CONFIG, WET_GRIP_OPTIONS, YES_NO_OPTIONS } from './constants/tyre-dropdown-options';
import { FormUtils } from 'src/app/core/utils/reactive-form';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { TypedFormGroup, ErrorMessage } from 'src/app/core/forms/engine/core-types';
import { ADD_STOCK_FORM_CONFIG } from './forms/add-stock-form.config';
import { FormBuilderService } from 'src/app/core/forms/engine/form-builder.service';
import { StorageResponse, StorageService } from 'src/app/pages/storage/services/storage.service';
import { ProductsService } from 'src/app/services/products.service';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';
import { ProductCategoryEnum } from 'src/app/core/enums/product-category.enum';
import { StoreStockLotsService } from '../../services/store-stock-lots.service';
import { ProductItemCategoryId, TireProduct } from 'src/app/core/interfaces/products-types/tire-product.interface';
import { ProductCategoryService } from 'src/app/services/product-category.service';
import { ProductCategory, ProductSubCategory } from 'src/app/core/interfaces/product.interface';


@Component({
  selector: 'app-add-stock-manually-modal',
  templateUrl: './add-stock-manually-modal.component.html',
  styleUrls: ['./add-stock-manually-modal.component.scss']
})
export class AddStockManuallyModalComponent implements OnInit, OnDestroy {
  stockForm!: TypedFormGroup<ProductLotEntry>;

  isSubmitting = false;
  productType: 'existing' | 'new' = 'existing';
  stores: StorageResponse[] | null = [];
  categories: ProductCategory[] | null = [];
  subcategories: ProductSubCategory[] | null = [];
  filteredProducts: ProductItemCategoryId[] = [];
  isLoadingProducts = false;
  showProductDropdown = false;

  private readonly destroy$ = new Subject<void>();
  private formDestroy$ = new Subject<void>();

  readonly qualityOptions = QUALITY_OPTIONS;
  readonly seasonOptions = SEASON_OPTIONS;
  readonly yesNoOptions = YES_NO_OPTIONS;
  readonly rollingResistanceOptions = ROLLING_RESISTANCE_OPTIONS;
  readonly wetGripOptions = WET_GRIP_OPTIONS;
  readonly noiseCategoryOptions = NOISE_CATEGORY_OPTIONS;


  constructor(
    private readonly formBuilderService: FormBuilderService,
    private readonly productsService: ProductsService,
    private readonly tiresService: TiresService,
    private readonly productCategoryService: ProductCategoryService,
    private readonly storageService: StorageService,
    public dialogRef: MatDialogRef<AddStockManuallyModalComponent>,
    private readonly storeStockLotsService: StoreStockLotsService,
    private readonly translate: TranslateService,
    private readonly snackBar: MatSnackBar,
    private readonly snackBarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: AddStockDialogData,
    private readonly analytics: AnalyticsService
  ) {
    this.stockForm = this.createForm();
    this.setFormListeners();
  }

  createForm(productData?: TireProduct): TypedFormGroup<ProductLotEntry> {
    return this.formBuilderService.create<ProductLotEntry>(
      ADD_STOCK_FORM_CONFIG,
      { initialValue: this.buildInitialValue(productData) ?? undefined }
    );
  }


  private buildInitialValue(productData?: TireProduct): ProductLotEntry | null {
    let currentValue: ProductLotEntry = this.stockForm?.getRawValue();

    if (!productData) {
      return currentValue ? { ...currentValue, newTireProduct: this.getEmptyProduct() as TireProduct } : null;
    }

    if (productData?.productCategoryId) {
      currentValue.productCategoryId = productData?.productCategoryId
    }

    return { ...currentValue, newTireProduct: this.sanitizeProductData(productData) };
  }

  private getEmptyProduct(): Partial<TireProduct> {
    return {
      code: '',
      name: '',
      description: '',
      brand: '',
      identifier: '',
      model: '',
      width: undefined,
      height: undefined,
      radius: undefined,
      loadIndex: '',
      speedIndex: '',
      rollingResistance: undefined,
      wetGrip: undefined,
      noiseLevel: undefined,
      threePMSF: false,
      extraload: false,
      runflat: false,
      tireSeason: undefined
    };
  }

  private sanitizeProductData(productData: TireProduct): TireProduct {
    return {
      ...productData,
      productCategoryId: this.checkExistingCategoryId(productData.productCategoryId, this.categories) as ProductCategoryEnum,
      subCategoryGuid: this.checkExistingSubCategoryId(productData.subCategoryGuid, this.subcategories)
    };
  }

  private resetProductSearchState(): void {
    this.filteredProducts = [];
    this.showProductDropdown = false;
    this.isLoadingProducts = false;
  }

  private setFormListeners(): void {
    combineLatest([
      this.stockForm.controls.productSearch.valueChanges.pipe(startWith('')),
      this.stockForm.controls.brandSearch.valueChanges.pipe(startWith(''))
    ])
      .pipe(
        debounceTime(300),
        takeUntil(this.formDestroy$)
      )
      .subscribe(([productSearch, brandSearch]) => {
        this.findProductByBrandAndProduct(productSearch ?? '', brandSearch ?? '');
      });

    this.stockForm.controls.productCategoryId.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.formDestroy$)
      )
      .subscribe((categoryId) => {
        const newTireProduct = this.stockForm.get('newTireProduct');
        if (newTireProduct && categoryId) {
          newTireProduct.patchValue({ productCategoryId: categoryId });
        }
      });
  }

  ngOnInit(): void {
    this.setInitialData();
  }

  async setInitialData(): Promise<void> {
    try {
      const [stores, categories, subcategories] = await Promise.all([
        this.fetchStores(),
        this.fetchCategories(),
        this.fetchSubcategories()
      ]);

      this.stores = stores ?? [];
      this.categories = categories ?? [];
      this.subcategories = subcategories ?? [];

      this.initializeFormData();
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.formDestroy$.next();
    this.formDestroy$.complete();
  }

  private translateOptionArray(options: readonly ValueLabelOption[]): ValueLabelOption[] {
    return options.map(opt => ({
      value: opt.value,
      label: this.translate.instant(opt.label)
    }));
  }

  markFormAsTouched(): void {
    this.stockForm.markAllAsTouched();
  }

  updateSearcherBrand(brand: string): void {
    this.stockForm.controls.brandSearch?.setValue(brand);
  }


  // ============================================
  // PRODUCT SEARCH METHODS - Don't Repeat Yourself
  // ============================================

  /**
   * Busca productos por marca y código
   * Actualiza el dropdown de productos con los resultados
   */
  findProductByBrandAndProduct(productSearch: string, brandSearch: string): void {
    const normalizedTerm = `${productSearch}`.trim();
    if (!normalizedTerm && !brandSearch) {
      this.resetProductSearchState();
      return;
    }

    this.executeProductSearch(
      () => this.productsService.getGenericProducts(normalizedTerm, brandSearch ? [brandSearch] : null, this.stockForm.controls.productCategoryId.value),
    );
  }


  private executeProductSearch(
    searchFn: () => Observable<ProductItemCategoryId[]>,
  ): void {
    this.showProductDropdown = true;
    this.isLoadingProducts = true;
    this.filteredProducts = [];

    searchFn()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: ProductItemCategoryId[]) => {
          this.isLoadingProducts = false;
          this.filteredProducts = [...data];
        },
        error: () => {
          this.isLoadingProducts = false;
          this.filteredProducts = [];
        }
      });
  }

  selectProductFromDropdown(product: ProductItemCategoryId): void {
    this.stockForm.controls.productId.setValue(product.productId ?? null);
    this.loadProductById(product.productId)
  }

  setTireProduct(product: TireProduct) {
    this.cleanReloadFormSubject()
    this.stockForm = this.createForm(product);
    this.setFormListeners();
    this.closeAllDropdowns();
  }

  cleanReloadFormSubject() {
    this.formDestroy$.next();
    this.formDestroy$.complete();
    this.formDestroy$ = new Subject<void>();
  }

  private closeAllDropdowns(): void {
    this.showProductDropdown = false;
  }


  checkExistingCategoryId(categoryId: number, categories?: ProductCategory[] | null): ProductCategoryEnum | undefined {
    if (!categories) { return undefined }
    return categories?.some((x: ProductCategory) => { return x.productCategoryId === categoryId }) ? categoryId : undefined
  }

  checkExistingSubCategoryId(subcategoryId: string, subcategories: ProductSubCategory[] | null): string {
    if (!subcategories) { return '' }
    return subcategories?.some((x: ProductSubCategory) => { return x.subCategoryId === subcategoryId }) ? subcategoryId : ''
  }


  private initializeFormData(): void {
    if (this.data?.selectedStoreId) {
      this.stockForm.patchValue({ storeId: this.data.selectedStoreId });
    }
    if (this.data?.productType) {
      this.productType = this.data.productType;
    }
    if (this.data?.selectedProductId && this.productType === 'existing') {
      this.stockForm.patchValue({ productId: this.data.selectedProductId });
      this.loadProductById(this.data.selectedProductId);
    }
  }

  private async fetchCategories(): Promise<ProductCategory[] | null> {
    try {
      const categories: ProductCategory[] = await lastValueFrom(this.productCategoryService.getProductCategories());
      return categories?.filter((x) => { return x.productCategoryId === ProductCategoryEnum.TIRES });
    } catch (error) {
      console.error('Error fetching categories:', error);
      this.snackBarService.showError("Error fetching categories")
      return null;
    }
  }

  private async fetchSubcategories(): Promise<ProductSubCategory[] | null> {
    try {
      const subcategoriesRaw: ProductSubCategory[] = await lastValueFrom(this.productCategoryService.getSubCategoriesByCategoryIds([ProductCategoryEnum.TIRES]));
      const subcategories = subcategoriesRaw
        .sort((a, b) => a.subCategoryName.localeCompare(b.subCategoryName));
      return subcategories;
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      this.snackBarService.showError("Error fetching subcategories")
      return null;
    }
  }

  private async fetchStores(): Promise<StorageResponse[] | null> {
    try {
      const stores: StorageResponse[] = await firstValueFrom(
        this.storageService.getAllStorages()
      );
      return stores;
    } catch (error) {
      console.error('Error loading stores:', error);
      this.snackBarService.showError("Error loading stores")
      return null;
    }
  }

  private loadProductById(productId: number): void {
    this.tiresService.getTireProductById(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response) {
            this.setTireProduct(response)
          }
        },
        error: (error: unknown) => {
          console.error('Error loading product:', error);
          this.snackBarService.showError("Error loading product")
        }
      });
  }

  clearProductSelection(): void {
    this.stockForm.patchValue({
      productSearch: '',
      productId: null,
    });
    this.stockForm = this.createForm()
    this.setFormListeners();
    this.resetProductSearchState();

  }


  setProductType(type: 'existing' | 'new'): void {
    this.productType = type;
    if (type === 'new') {
      this.stockForm.patchValue({ productId: null, productSearch: '' });
      this.filteredProducts = [];
      this.showProductDropdown = false;
    }
  }

  onSubmit(): void {
    if (this.stockForm.invalid || this.isSubmitting) {
      FormUtils.scrollToFirstError(this.stockForm)
      return;
    }
    const formValue = this.stockForm.getRawValue()
    this.createStockLot(formValue);
  }


  async createProduct(lotForm: ProductLotEntry): Promise<number | null> {
    switch (lotForm.productCategoryId) {
      case ProductCategoryEnum.TIRES:
        return firstValueFrom(this.tiresService.createTireProduct(lotForm.newTireProduct))
      default:
        return null;
    }
  }

  handleProductIdNotFoundError() {
    this.snackBarService.showError(
      "Unknown product type"
    )
  }

  private async createStockLot(formValue: ProductLotEntry): Promise<void> {
    this.isSubmitting = true;

    if (!formValue.productId) {
      const productId = await this.createProduct(formValue)
      if (!productId) { this.handleProductIdNotFoundError(); this.isSubmitting = false; return; }
      formValue.productId = productId;
    }

    const request: SaveLotDTO = this.buildStockRequest(formValue);

    this.analytics.track(AnalyticsEvent.StockAddManualClick, {
      ...this.analytics.globalProps(),
      warehouse_id: request.storeId,
    });
    this.storeStockLotsService
      .createStockLot(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: (result) => {
          this.snackBarService.showSuccess(this.translate.instant('addStock.stock_add_success'))
          this.dialogRef.close(true);
        },
        error: (error: unknown) => {
          console.error('❌ Error al crear stock:', error);
          this.isSubmitting = false;
          this.handleStockCreationError(error);
        },
        complete: () => {
        }
      });
  }

  private buildStockRequest(formValue: ProductLotEntry): SaveLotDTO {
    return {
      storeId: formValue.storeId!,
      productId: formValue.productId!,
      stockQuantity: formValue.quantity,
      price: formValue.price,
      location: formValue.location ?? undefined,
      dot: formValue.dot ?? undefined,
      entryDate: new Date(formValue.entryDate).toISOString(), // ISO string (ej: 2026-01-20T00:00:00.000Z)
    };
  }


  private handleStockCreationError(error: unknown): void {

    const errorMessage = this.getErrorMessage(error);
    this.showErrorMessage(errorMessage);
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return this.getHttpErrorMessage(error);
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Error de conexión. Verifica tu internet';
  }

  private getHttpErrorMessage(error: HttpErrorResponse): string {

    switch (error.status) {
      case HTTP_STATUS.CONFLICT:
        return 'El producto ya existe en el inventario';

      case HTTP_STATUS.BAD_REQUEST:
        return error.error?.message || 'Error en la validación de los datos';

      case HTTP_STATUS.UNAUTHORIZED:
      case HTTP_STATUS.FORBIDDEN:
        return 'No tienes permisos para realizar esta acción';

      case HTTP_STATUS.NO_CONNECTION:
        return 'Error de conexión. Verifica tu internet';

      default:
        if (error.status >= 500) {
          return 'Error en el servidor. Por favor intenta más tarde';
        }
        return `Error ${error.status}: ${error.error?.message || 'Ocurrió un error inesperado'}`;
    }
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(
      message,
      this.translate.instant('common.close'),
      SNACKBAR_CONFIG
    );
  }



  onCancel(): void {
    this.dialogRef.close();
  }

  get dialogTitle(): string {
    return this.data?.title || 'Agregar Stock';
  }

  get selectedStore(): StorageResponse | undefined {
    const storeId = this.stockForm.controls.storeId.value;
    return storeId !== null && storeId !== undefined
      ? this.stores?.find(store => store.id === storeId)
      : undefined;
  }

  incrementQuantity(): void {
    const currentValue = this.stockForm.controls.quantity.value ?? 0;
    this.stockForm.patchValue({ quantity: currentValue + 1 });
  }

  decrementQuantity(): void {
    const currentValue = this.stockForm.controls.quantity.value ?? 0;
    this.stockForm.patchValue({ quantity: Math.max(1, currentValue - 1) });
  }



}